import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Alert } from '@mui/material';
import { toast } from 'react-toastify';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvSelect } from '../InvField';
import StockBalanceAutocomplete from '../StockBalanceAutocomplete';
import { changeStockBinLocation } from '../../api/stockAdjustment';
import { getStores, getStoreBins } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { storeDisplayName } from '../../utils/storeEntity';
import { IPD_COLORS } from '../../theme';

/**
 * Immediate change-bin form — PUT /inventory/stock/bin-location/
 * Quantity unchanged; no ledger entry.
 */
export default function ChangeBinPanel() {
  const [stores, setStores] = useState([]);
  const [bins, setBins] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [itemKey, setItemKey] = useState('');
  const [catalogueItem, setCatalogueItem] = useState(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [grnItemId, setGrnItemId] = useState('');
  const [targetBinId, setTargetBinId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setTargetBinId('');
    setBins([]);
    if (!storeId) return;
    getStoreBins(storeId)
      .then(d => setBins(toInventoryList(d).filter(b => b.is_active !== false)))
      .catch(() => setBins([]));
  }, [storeId]);

  const batchOptions = (catalogueItem?.batches || [])
    .filter(b => !b.is_expired)
    .map(b => ({
      value: b.batch_number || '',
      label: `${b.batch_number || '—'} · Avail ${b.available_quantity ?? '—'}`,
      grn_item_id: b.grn_item_id || '',
    }))
    .filter(o => o.value);

  function onItemChange(key, item) {
    setItemKey(key || '');
    setCatalogueItem(item);
    setBatchNumber('');
    setGrnItemId('');
  }

  function onBatchChange(value) {
    setBatchNumber(value);
    const match = batchOptions.find(o => o.value === value);
    setGrnItemId(match?.grn_item_id || '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!storeId || !catalogueItem?.item_id || !targetBinId) {
      toast.error('Store, item, and target bin are required');
      return;
    }
    setSaving(true);
    try {
      // API: PUT /inventory/stock/bin-location/
      await changeStockBinLocation({
        store_id: storeId,
        item_id: catalogueItem.item_id,
        target_bin_id: targetBinId,
        batch_number: batchNumber || undefined,
        grn_item_id: grnItemId || undefined,
      });
      toast.success('Bin location updated — quantity unchanged');
      setItemKey('');
      setCatalogueItem(null);
      setBatchNumber('');
      setGrnItemId('');
      setTargetBinId('');
    } catch (err) {
      toast.error(err.message || 'Failed to change bin');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = stores.map(s => ({
    value: s.store_id || s.id,
    label: storeDisplayName(s),
  }));
  const binOptions = bins.map(b => ({
    value: b.bin_id || b.id,
    label: b.bin_name || b.bin_code || b.bin_id || b.id,
  }));

  return (
    <SectionCard
      title="Change Bin Location"
      subtitle="Moves stock to another bin. Quantity and ledger are not affected."
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Physical placement only — no inventory quantity change and no ledger entry.
      </Alert>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <InvSelect
              label="Store *"
              value={storeId}
              onChange={e => {
                setStoreId(e.target.value);
                onItemChange('', null);
              }}
              options={storeOptions}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <StockBalanceAutocomplete
              storeId={storeId}
              value={itemKey}
              onChange={onItemChange}
              label="Item *"
              placeholder="Search item (min 2 chars)…"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InvSelect
              label="Batch"
              value={batchNumber}
              onChange={e => onBatchChange(e.target.value)}
              options={[{ value: '', label: '—' }, ...batchOptions]}
              disabled={!catalogueItem}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InvSelect
              label="Target bin *"
              value={targetBinId}
              onChange={e => setTargetBinId(e.target.value)}
              options={binOptions}
              disabled={!storeId}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <InvButton type="submit" loading={saving} disabled={!storeId || !catalogueItem || !targetBinId}>
              Change bin
            </InvButton>
          </Grid>
        </Grid>
        {catalogueItem && (
          <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary, mt: 2 }}>
            Selected: {catalogueItem.item_name || catalogueItem.item_code}
            {' · '}
            Total avail: {catalogueItem.total_available_quantity ?? '—'}
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
}
